import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../components/ui/navigation-menu';
import { Menu, X, User, LogOut, BookOpen, FileText, Settings, Users, ChevronDown, Search } from 'lucide-react';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="navbar" className="sticky top-0 z-50 w-full bg-white border-b shadow-sm">
      {/* Top bar */}
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex justify-between items-center text-xs">
          <span>Journal de pratiques et recherches en psychothérapie</span>
          <div className="flex items-center gap-4">
            {user ? (
              <span>Connecté en tant que {user.name}</span>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="hover:underline">Connexion</Link>
                <span>|</span>
                <Link to="/register" className="hover:underline">S'inscrire</Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex h-24 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/logo.png" alt="JPRP" className="w-[72px] h-[72px] rounded" />
            <div className="hidden sm:block">
              <span className="font-serif font-bold text-lg text-foreground block leading-tight">
                JPRP
              </span>
              <span className="text-xs text-muted-foreground">
                Psychothérapie & Recherche
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive('/') ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
              }`}
            >
              Accueil
            </Link>

            {/* Journal Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-4 py-2 text-sm font-medium rounded-md transition-colors text-foreground hover:bg-muted flex items-center gap-1">
                  Journal
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/about')}>
                  À propos du journal
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/editorial-board')}>
                  Comité éditorial
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/author-guidelines')}>
                  Instructions aux auteurs
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/articles"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive('/articles') ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
              }`}
            >
              Articles
            </Link>

            <Link
              to="/resources"
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive('/resources') ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
              }`}
            >
              Ressources
            </Link>
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/articles')} className="gap-2">
              <Search className="w-4 h-4" />
              Rechercher
            </Button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-sm text-primary-foreground font-medium">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <FileText className="mr-2 h-4 w-4" />
                    Tableau de bord
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    Mon profil
                  </DropdownMenuItem>
                  {(user.role === 'reviewer' || user.role === 'admin') && (
                    <DropdownMenuItem onClick={() => navigate('/review')} data-testid="menu-review">
                      <Users className="mr-2 h-4 w-4" />
                      Évaluations
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Settings className="mr-2 h-4 w-4" />
                      Administration
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/editor/new">
                <Button size="sm" data-testid="submit-btn">
                  Soumettre un article
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-md ${
                  isActive('/') ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                Accueil
              </Link>
              <Link
                to="/articles"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-md ${
                  isActive('/articles') ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                Articles
              </Link>
              <Link
                to="/resources"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-md ${
                  isActive('/resources') ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                Ressources
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-md ${
                  isActive('/about') ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                À propos
              </Link>
              <Link
                to="/editorial-board"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-md ${
                  isActive('/editorial-board') ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                Comité éditorial
              </Link>
              <Link
                to="/author-guidelines"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium rounded-md ${
                  isActive('/author-guidelines') ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                Instructions aux auteurs
              </Link>

              <div className="border-t my-2 pt-2">
                {user ? (
                  <div className="flex flex-col gap-1">
                    <div className="px-4 py-2 text-sm text-muted-foreground">
                      Connecté : {user.name}
                    </div>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-3 text-sm font-medium"
                    >
                      Tableau de bord
                    </Link>
                    <Link
                      to="/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-3 text-sm"
                    >
                      Mon profil
                    </Link>
                    {(user.role === 'reviewer' || user.role === 'admin') && (
                      <Link
                        to="/review"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 text-sm text-purple-600 font-medium"
                      >
                        Évaluations
                      </Link>
                    )}
                    {user.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 text-sm text-primary font-medium"
                      >
                        Administration
                      </Link>
                    )}
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="px-4 py-3 text-sm text-destructive text-left"
                    >
                      Déconnexion
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 px-4">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full">Connexion</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full">S'inscrire</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
